import { useState } from "react";

export const DraggableColumnList = ({ columns, tempColumns, onCheckChange, onReorder }) => {
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        setDragOverItem(index);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverItem(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();

        const dragIndex = draggedItem;
        if (dragIndex === null || dragIndex === dropIndex) {
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        // Get the dragged column and drop target column
        const draggedCol = orderedNonMandatoryColumns[dragIndex];
        const dropCol = orderedNonMandatoryColumns[dropIndex];

        // Only allow reordering if both columns are checked (visible)
        if (!tempColumns?.includes(draggedCol.label) || !tempColumns?.includes(dropCol.label)) {
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        // Get all non-mandatory columns that are checked (visible) in their current order
        const visibleColumns = orderedNonMandatoryColumns
            .filter(col => tempColumns?.includes(col.label))
            .map(col => col.label);

        // Get the indices within the visible columns array
        const visibleDragIndex = visibleColumns.findIndex(label => label === draggedCol.label);
        const visibleDropIndex = visibleColumns.findIndex(label => label === dropCol.label);

        // Reorder the visible columns array
        const newOrder = [...visibleColumns];
        const [removed] = newOrder.splice(visibleDragIndex, 1);
        newOrder.splice(visibleDropIndex, 0, removed);

        // Get mandatory columns
        const mandatoryColumns = columns
            .filter(col => col.mandatory)
            .map(col => col.label);

        // Update tempColumns
        onReorder([...mandatoryColumns, ...newOrder]);

        setDraggedItem(null);
        setDragOverItem(null);
    };

    // Get all non-mandatory columns
    const allNonMandatoryColumns = columns.filter(col => !col.mandatory);

    // Create ordered list based on tempColumns order
    const orderedNonMandatoryColumns = [...allNonMandatoryColumns].sort((a, b) => {
        const aChecked = tempColumns?.includes(a.label);
        const bChecked = tempColumns?.includes(b.label);

        // If both are checked, sort based on tempColumns order
        if (aChecked && bChecked) {
            const aIndex = tempColumns.indexOf(a.label);
            const bIndex = tempColumns.indexOf(b.label);
            return aIndex - bIndex;
        }

        // If only a is checked, a should come before b
        if (aChecked && !bChecked) return -1;

        // If only b is checked, b should come before a
        if (!aChecked && bChecked) return 1;

        // If both are unchecked, maintain original order
        return allNonMandatoryColumns.indexOf(a) - allNonMandatoryColumns.indexOf(b);
    });

    return (
        <ul
            className="list-unstyled mb-0 ps-2 bar"
            style={{
                height: "14rem",
                overflowY: "auto",
                overflowX: "hidden",
                paddingTop: "8px",
                paddingBottom: "15px",
                margin: 0,
            }}
        >
            {orderedNonMandatoryColumns.map((col, index) => {
                const isChecked = tempColumns?.includes(col.label);

                return (
                    <li
                        key={col.id}
                        draggable={isChecked}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{
                            padding: "10px 8px",
                            marginBottom: "4px",
                            listStyle: "none",
                            backgroundColor: isChecked ? '#ffffff' : '#f8f9fa',
                            border: '1px solid',
                            borderColor: dragOverItem === index ? '#007bff' : '#e9ecef',
                            borderTop: dragOverItem === index ? '2px solid #007bff' : '1px solid #e9ecef',
                            borderRadius: '4px',
                            opacity: draggedItem === index ? 0.5 : 1,
                            cursor: isChecked ? 'move' : 'default',
                            transition: 'all 0.1s ease',
                            color: isChecked ? 'inherit' : '#999',
                        }}
                    >
                        <div className="d-flex align-items-center">
                            {isChecked ? (
                                <span
                                    style={{
                                        marginRight: '12px',
                                        color: '#6c757d',
                                        fontSize: '16px',
                                        cursor: 'grab'
                                    }}
                                >
                                    ⋮⋮
                                </span>
                            ) : (
                                <span style={{ width: '28px' }}></span>
                            )}
                            <input
                                className="form-check-input column-checkboxs"
                                type="checkbox"
                                id={col.label}
                                checked={isChecked}
                                onChange={() => onCheckChange(col)}
                                style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                }}
                            />
                            <label
                                className="form-check-label flex-grow-1 mb-0"
                                htmlFor={col.label}
                                style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    marginBottom: 0,
                                    textDecoration: isChecked ? 'none' : 'line-through',
                                    opacity: isChecked ? 1 : 0.7,
                                }}
                            >
                                {col.label}
                            </label>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};